import {
  AlertSetting,
  AlertType,
  ComplianceItemProgress,
  ComplianceUserItemJoin,
} from 'src/v3/core/types'

export type ComplianceWizardStep =
  'details' | 'requirements' | 'users' | 'review'

export type ComplianceLicenseItemDraft = {
  id: string
  state: string
  type: string
}

export type ComplianceCredentialItemDraft = {
  id: string
  type: string
  customType?: string
}

export type ComplianceNREMTItemDraft = {
  id: string
}

export type ComplianceRecertCycleItemDraft = {
  id: string
  cycleType: 'license' | 'credential' | 'nremt'
  templateID?: string
  licenseType?: string
  state?: string
  credentialType?: string
  customCredentialType?: string
}

export type ComplianceFormData = {
  name: string
  linkedServiceID: string
  // Optional while requirement dates are phased out: create no longer sets them; edit still loads them.
  issueDate?: string
  expirationDate?: string
  alertSetting: AlertSetting
  alertType: AlertType
  userIDs: string[]
  licenseItems: ComplianceLicenseItemDraft[]
  credentialItems: ComplianceCredentialItemDraft[]
  nremtItems: ComplianceNREMTItemDraft[]
  recertItems: ComplianceRecertCycleItemDraft[]
}

export type ComplianceLicenseItemPayload = {
  id: string
  state: string
  type: string
}

export type ComplianceCredentialItemPayload = {
  id: string
  type: string | { other: string }
}

export type ComplianceNREMTItemPayload = {
  id: string
}

export type ComplianceRecertCycleItemPayload = {
  id: string
  cycleType:
    | 'nremt'
    | { license: { type: string; state: string } }
    | { credential: { type: string | { other: string } } }
  templateID?: string
}

export type CreateCompliancePayload = {
  name: string
  linkedServiceID: string
  // issueDate: string
  // expirationDate: string
  alertSetting: AlertSetting
  alertType: AlertType
  userIDs: string[]
  licenseItems: ComplianceLicenseItemPayload[]
  credentialItems: ComplianceCredentialItemPayload[]
  nremtItems: ComplianceNREMTItemPayload[]
  recertItems: ComplianceRecertCycleItemPayload[]
}

export type UpdateCompliancePayload = {
  name?: string
  expirationDate?: string
  alertSetting?: AlertSetting
  alertType?: AlertType
  userIDs?: string[]
  licenseItems?: ComplianceLicenseItemPayload[]
  credentialItems?: ComplianceCredentialItemPayload[]
  nremtItems?: ComplianceNREMTItemPayload[]
  recertItems?: ComplianceRecertCycleItemPayload[]
}

export const licenseTypeDict: Record<string, string> = {
  paramedic: 'Paramedic',
  'registered nurse': 'Registered Nurse',
  emr: 'Emergency Medical Responder',
  cfr: 'Certified First Responder',
  emt: 'EMT',
  'emt-1': 'EMT-1',
  'emt-b': 'EMT-B',
  'emt-d': 'EMT-D',
  aemt: 'AEMT',
  'emt-2': 'EMT-2',
  'emt-cc': 'EMT-CC',
  'advanced care paramedic (canada only)':
    'Advanced Care Paramedic (Canada Only)',
  'primary care paramedic (canada only)':
    'Primary Care Paramedic (Canada Only)',
  physician: 'Physician',
  'physician assistant': 'Physician Assistant',
  other: 'Other',
}

export let formatLicenseType = (type: string) =>
  licenseTypeDict[type.toLowerCase()] || type

export let formatCredentialType = (
  item: Pick<ComplianceCredentialItemDraft, 'type' | 'customType'>,
) =>
  item.type === 'Other' && item.customType
    ? item.customType
    : item.type.toUpperCase()

export let credentialItemFromPayload = (item: {
  id: string
  type: string | { other: string }
}): ComplianceCredentialItemDraft =>
  typeof item.type === 'string'
    ? { id: item.id, type: item.type }
    : { id: item.id, type: 'Other', customType: item.type.other }

export let credentialItemToPayload = (
  item: ComplianceCredentialItemDraft,
): ComplianceCredentialItemPayload => ({
  id: item.id,
  type: item.type === 'Other' ? { other: item.customType ?? '' } : item.type,
})

export let recertItemToPayload = (
  item: ComplianceRecertCycleItemDraft,
): ComplianceRecertCycleItemPayload => {
  if (item.cycleType === 'nremt')
    return { id: item.id, cycleType: 'nremt', templateID: item.templateID }
  if (item.cycleType === 'license')
    return {
      id: item.id,
      cycleType: {
        license: { type: item.licenseType ?? '', state: item.state ?? '' },
      },
      templateID: item.templateID,
    }
  let credType =
    item.credentialType === 'Other'
      ? { other: item.customCredentialType ?? '' }
      : (item.credentialType ?? '')
  return {
    id: item.id,
    cycleType: { credential: { type: credType } },
    templateID: item.templateID,
  }
}

export let recertItemFromPayload = (item: {
  id: string
  cycleType: ComplianceRecertCycleItemPayload['cycleType']
  templateID?: string
}): ComplianceRecertCycleItemDraft => {
  if (item.cycleType === 'nremt')
    return { id: item.id, cycleType: 'nremt', templateID: item.templateID }
  if (typeof item.cycleType === 'object' && 'license' in item.cycleType)
    return {
      id: item.id,
      cycleType: 'license',
      licenseType: item.cycleType.license.type,
      state: item.cycleType.license.state,
      templateID: item.templateID,
    }
  if (typeof item.cycleType === 'object' && 'credential' in item.cycleType) {
    let rawType = item.cycleType.credential.type
    if (typeof rawType === 'object' && 'other' in rawType)
      return {
        id: item.id,
        cycleType: 'credential',
        credentialType: 'Other',
        customCredentialType: rawType.other,
        templateID: item.templateID,
      }
    return {
      id: item.id,
      cycleType: 'credential',
      credentialType: rawType as string,
      templateID: item.templateID,
    }
  }
  return { id: item.id, cycleType: 'nremt', templateID: item.templateID }
}

let complianceItemTypeOrder: Record<string, number> = {
  nremt: 0,
  license: 1,
  credential: 2,
  recert: 3,
  unknown: 4,
}

let complianceItemSortKey = (itemType: string, label: string) =>
  `${complianceItemTypeOrder[itemType] ?? 4}:${label}`

export let sortComplianceItems = <
  T extends Pick<
    ComplianceItemProgress | ComplianceUserItemJoin,
    'itemType' | 'label'
  >,
>(
  items: T[],
) =>
  items.toSorted((a, b) =>
    complianceItemSortKey(a.itemType, a.label ?? '').localeCompare(
      complianceItemSortKey(b.itemType, b.label ?? ''),
    ),
  )

export let formatRecertCycleItemType = (
  item: ComplianceRecertCycleItemDraft,
): string => {
  if (item.cycleType === 'nremt') return 'NREMT'
  if (item.cycleType === 'license')
    return `${formatLicenseType(item.licenseType ?? '')} - ${item.state}`
  if (item.cycleType === 'credential') {
    if (item.credentialType === 'Other' && item.customCredentialType)
      return item.customCredentialType
    return (item.credentialType ?? '').toUpperCase()
  }
  return ''
}
